import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  Volume2,
  VolumeX,
  Megaphone,
  MegaphoneOff,
  Gem,
} from "lucide-react";
import { useVictoryMusic } from "@/hooks/useVictoryMusic";
import { GameResultsScreen } from "@/components/study/GameResultsScreen";
import { Card } from "@/types/linguadeck";
import { useGamification } from "@/contexts/GamificationContext";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { playPop, playSuccess, playError } from "@/lib/studySfx";

const CRYSTALS_PER_PAIR = 5;
const MAX_PAIRS_PER_LEVEL = 6;
const MAX_FLIPPED = 2;
const MAX_COMBO_MULTIPLIER = 5;
const FLIP_BACK_DELAY_MS = 1000;
const MATCH_SUCCESS_HOLD_MS = 700;
const LEVEL_UP_DISPLAY_MS = 1500;

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Floating +N crystals animation (spawns center, floats up, fades out). */
function FloatingCrystals({
  amount,
  onComplete,
}: {
  amount: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50"
      initial={false}
    >
      <motion.span
        className="inline-flex items-center gap-1.5 text-2xl font-bold text-[hsl(var(--accent2))] drop-shadow-lg whitespace-nowrap"
        initial={{ opacity: 1, y: 0, scale: 1 }}
        animate={{ opacity: 0, y: -80, scale: 1.2 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        onAnimationComplete={onComplete}
      >
        +{amount}
        <Gem className="w-6 h-6" aria-hidden />
      </motion.span>
    </motion.div>
  );
}

/** Brief "Level Up!" overlay. */
function LevelUpOverlay() {
  const { t } = useTranslation();
  return (
    <motion.div
      className="pointer-events-none fixed inset-0 flex items-center justify-center z-50 bg-[hsl(var(--card-bg)/0.9)]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.span
        className="text-2xl font-bold text-[hsl(var(--primary))] drop-shadow-lg"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        {t("memoryMix.levelUp", { defaultValue: "Level Up!" })}
      </motion.span>
    </motion.div>
  );
}

export interface MemoryTile {
  id: string;
  pairId: string;
  label: string;
  side: "term" | "translation";
  termLang?: string;
  /** Card for playAudio */
  card?: Card;
}

interface MemoryMixModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
}

export default function MemoryMixMode({
  cards,
  lang: deckLang,
  onBack,
  onProgressComplete,
}: MemoryMixModeProps) {
  const { t } = useTranslation();
  const { addCrystals, finishStudySession } = useGamification();
  const {
    isSoundEnabled,
    isVoiceEnabled,
    gameVolume,
    voiceVolume,
    setGameVolume,
    setVoiceVolume,
    toggleSound,
    toggleVoice,
  } = useSoundStore();

  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );

  /** Build tiles for a slice of cards (used per level). */
  const buildTilesForCards = useCallback(
    (cards: Card[]) => {
      const list: MemoryTile[] = [];
      cards.forEach((card, index) => {
        const pairId = `pair-${card.id ?? index}`;
        const termLang = card.frontLang ?? deckLang;
        list.push({
          id: `${pairId}-term`,
          pairId,
          label: (card.front || "").trim(),
          side: "term",
          termLang,
          card,
        });
        list.push({
          id: `${pairId}-translation`,
          pairId,
          label: (card.back || "").trim(),
          side: "translation",
          termLang,
          card,
        });
      });
      return list;
    },
    [deckLang]
  );

  const totalPairs = filteredCards.length;
  const totalLevels = Math.max(1, Math.ceil(totalPairs / MAX_PAIRS_PER_LEVEL));

  const [currentLevel, setCurrentLevel] = useState(1);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [tiles, setTiles] = useState<MemoryTile[]>([]);
  const [flippedTiles, setFlippedTiles] = useState<Set<string>>(new Set());
  const [matchedTiles, setMatchedTiles] = useState<Set<string>>(new Set());
  const [combo, setCombo] = useState(0);
  const [floatingCrystals, setFloatingCrystals] = useState<number | null>(null);
  const [sessionCrystals, setSessionCrystals] = useState(0);
  const [isGameFinished, setIsGameFinished] = useState(false);
  const [isFlippingBack, setIsFlippingBack] = useState(false);
  const [celebratingMatch, setCelebratingMatch] = useState<Set<string>>(new Set());
  const flipBackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useVictoryMusic({
    isGameFinished,
    isSoundEnabled,
    gameVolume,
  });

  useEffect(() => {
    if (isGameFinished) onProgressComplete?.(100);
  }, [isGameFinished, onProgressComplete]);

  const matchDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const levelUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const levelCompleteHandledRef = useRef(false);

  /** Cards for the current level only (max MAX_PAIRS_PER_LEVEL pairs). */
  const cardsForCurrentLevel = useMemo(() => {
    const start = (currentLevel - 1) * MAX_PAIRS_PER_LEVEL;
    return filteredCards.slice(start, start + MAX_PAIRS_PER_LEVEL);
  }, [filteredCards, currentLevel]);

  /** Tiles source for current level only — deterministic. */
  const levelTilesSource = useMemo(
    () => buildTilesForCards(cardsForCurrentLevel),
    [buildTilesForCards, cardsForCurrentLevel]
  );

  const pairsInCurrentLevel = cardsForCurrentLevel.length;

  /** Stable key for "this deck" so we only run initial shuffle when deck actually changes. */
  const deckKey = useMemo(
    () => filteredCards.map((c) => c.id ?? "").join(","),
    [filteredCards]
  );

  const matchedPairsInLevel = matchedTiles.size / 2;
  const levelComplete = pairsInCurrentLevel > 0 && matchedPairsInLevel >= pairsInCurrentLevel;

  /** Tiles still in the grid — filter only. Same order as `tiles`; matched pairs removed. */
  const activeTiles = useMemo(
    () => tiles.filter((t) => !matchedTiles.has(t.id)),
    [tiles, matchedTiles]
  );

  /** Initialize or re-initialize tiles when deck or level changes. */
  useEffect(() => {
    if (levelTilesSource.length === 0) return;
    setTiles(shuffleArray(levelTilesSource));
    setFlippedTiles(new Set());
    setMatchedTiles(new Set());
    setCombo(0);
    setCelebratingMatch(new Set());
  }, [deckKey, currentLevel]);

  /** When deck changes, reset to level 1. */
  useEffect(() => {
    setCurrentLevel(1);
    setShowLevelUp(false);
    levelCompleteHandledRef.current = false;
  }, [deckKey]);

  /** Level complete: show Level Up then advance. Tiles reset via effect when currentLevel changes. */
  useEffect(() => {
    if (!levelComplete || currentLevel >= totalLevels) return; // finish handled in match callback
    if (levelCompleteHandledRef.current) return;
    levelCompleteHandledRef.current = true;
    setShowLevelUp(true);
    levelUpTimeoutRef.current = setTimeout(() => {
      setShowLevelUp(false);
      setCurrentLevel((l) => l + 1);
      levelCompleteHandledRef.current = false;
      levelUpTimeoutRef.current = null;
    }, LEVEL_UP_DISPLAY_MS);
    return () => {
      if (levelUpTimeoutRef.current) clearTimeout(levelUpTimeoutRef.current);
    };
  }, [levelComplete, currentLevel, totalLevels]);

  useEffect(() => {
    return () => {
      if (flipBackTimeoutRef.current) clearTimeout(flipBackTimeoutRef.current);
      if (matchDelayRef.current) clearTimeout(matchDelayRef.current);
      if (levelUpTimeoutRef.current) clearTimeout(levelUpTimeoutRef.current);
    };
  }, []);

  const handlePlayAgain = useCallback(() => {
    setIsGameFinished(false);
    setSessionCrystals(0);
    setCurrentLevel(1);
    setShowLevelUp(false);
    levelCompleteHandledRef.current = false;
  }, []);

  const handleBackToDeck = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const handleTileClick = useCallback(
    (tile: MemoryTile) => {
      if (matchedTiles.has(tile.id) || isFlippingBack || celebratingMatch.size > 0) return;
      if (flippedTiles.has(tile.id)) return;
      if (flippedTiles.size >= MAX_FLIPPED) return;

      if (isSoundEnabled) playPop(gameVolume * 0.6);

      const nextFlipped = new Set(flippedTiles).add(tile.id);
      setFlippedTiles(nextFlipped);

      if (nextFlipped.size < MAX_FLIPPED) return;

      const flippedList = Array.from(nextFlipped);
      const tile1 = tiles.find((t) => t.id === flippedList[0]);
      const tile2 = tiles.find((t) => t.id === flippedList[1]);
      if (!tile1 || !tile2) return;

      if (tile1.pairId === tile2.pairId) {
        const comboMultiplier = Math.max(
          1,
          Math.min(combo + 1, MAX_COMBO_MULTIPLIER)
        );
        const baseAmount = CRYSTALS_PER_PAIR * comboMultiplier;
        const rewardAmount = baseAmount * currentLevel;
        if (isSoundEnabled) playSuccess(gameVolume * 0.7);
        const termTile = tile1.side === "term" ? tile1 : tile2;
        if (isVoiceEnabled && termTile.label && termTile.card) {
          const side = termTile.side === "term" ? "front" : "back";
          setTimeout(
            () => void playAudio({
              card: termTile.card!,
              side,
              deckLang: termTile.termLang ?? deckLang,
              volume: voiceVolume,
            }),
            300
          );
        }
        const result = addCrystals(rewardAmount, t("memoryMix.rewardReason"), {
          isLearningAction: true,
          silent: true,
        });
        const totalAdd = typeof result === "object" ? result.totalAdd : rewardAmount;
        setFloatingCrystals(totalAdd);
        setSessionCrystals((prev) => prev + totalAdd);
        setCelebratingMatch(new Set([tile1.id, tile2.id]));
        matchDelayRef.current = setTimeout(() => {
          setMatchedTiles((prev) =>
            new Set(prev).add(tile1.id).add(tile2.id)
          );
          setCelebratingMatch(new Set());
          setFlippedTiles(new Set());
          setCombo((c) => c + 1);
          matchDelayRef.current = null;
          const newMatchedInLevel = matchedPairsInLevel + 1;
          const totalCards = filteredCards.length;
          if (totalCards > 0 && onProgressComplete) {
            const newMatchedPairs = matchedTiles.size / 2 + 1;
            const pct = Math.min(100, Math.round((newMatchedPairs / totalCards) * 100));
            onProgressComplete(pct);
          }
          if (newMatchedInLevel >= pairsInCurrentLevel && currentLevel >= totalLevels) {
            finishStudySession(totalPairs, false);
            setIsGameFinished(true);
          }
        }, MATCH_SUCCESS_HOLD_MS);
      } else {
        setCombo(0);
        if (isSoundEnabled) playError(gameVolume * 0.8);
        setIsFlippingBack(true);
        flipBackTimeoutRef.current = setTimeout(() => {
          setFlippedTiles(new Set());
          setIsFlippingBack(false);
          flipBackTimeoutRef.current = null;
        }, FLIP_BACK_DELAY_MS);
      }
    },
    [
      tiles,
      flippedTiles,
      matchedTiles,
      isFlippingBack,
      celebratingMatch.size,
      matchedPairsInLevel,
      pairsInCurrentLevel,
      currentLevel,
      totalLevels,
      totalPairs,
      combo,
      addCrystals,
      finishStudySession,
      isSoundEnabled,
      onProgressComplete,
      filteredCards,
      matchedTiles,
      isVoiceEnabled,
      gameVolume,
      voiceVolume,
      deckLang,
      t,
    ]
  );

  const headerRow = (
    <header className="flex justify-between items-center w-full max-w-2xl mx-auto mb-6 gap-4">
      <div className="min-w-0 flex-1">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium transition-colors"
            aria-label={t("studyMenu.backToDeck")}
          >
            <ChevronLeft className="w-4 h-4 shrink-0" aria-hidden />
            {t("studyMenu.backToDeck")}
          </button>
        ) : (
          <div />
        )}
      </div>
      <div className="grid grid-cols-[64px_auto] gap-x-2 gap-y-1 items-center shrink-0">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={gameVolume}
          onChange={(e) => setGameVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.gameVolume")}
        />
        <button
          type="button"
          onClick={toggleSound}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isSoundEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={isSoundEnabled ? t("common.muteMusic") : t("common.unmuteMusic")}
        >
          {isSoundEnabled ? (
            <Volume2 className="w-5 h-5" />
          ) : (
            <VolumeX className="w-5 h-5" />
          )}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={voiceVolume}
          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
          className="w-16 h-1.5 accent-[hsl(var(--primary))] cursor-pointer justify-self-end"
          aria-label={t("common.voiceVolume")}
        />
        <button
          type="button"
          onClick={toggleVoice}
          className={`p-2 rounded-lg hover:bg-[hsl(var(--bg3))] transition-colors w-9 h-9 flex items-center justify-center ${isVoiceEnabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))] opacity-50"}`}
          aria-label={isVoiceEnabled ? t("common.voiceModeOn") : t("common.voiceModeOff")}
        >
          {isVoiceEnabled ? (
            <Megaphone className="w-5 h-5" />
          ) : (
            <MegaphoneOff className="w-5 h-5" />
          )}
        </button>
      </div>
    </header>
  );

  if (filteredCards.length === 0) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto px-4">
        {headerRow}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 text-center">
          <p className="text-lg font-medium text-[hsl(var(--foreground))]">
            {t("memoryMix.needCards")}
          </p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            {t("memoryMix.addMoreCards")}
          </p>
        </div>
      </div>
    );
  }

  if (isGameFinished) {
    return (
      <GameResultsScreen
        sessionCrystals={sessionCrystals}
        onPlayAgain={handlePlayAgain}
        onBackToDeck={handleBackToDeck}
        headerRow={headerRow}
      />
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full max-w-2xl mx-auto px-4 relative">
      <AnimatePresence>
        {floatingCrystals != null && (
          <FloatingCrystals
            amount={floatingCrystals}
            onComplete={() => setFloatingCrystals(null)}
          />
        )}
        {showLevelUp && <LevelUpOverlay />}
      </AnimatePresence>

      {headerRow}

      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap shrink-0">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">
          {t("memoryMix.progressFull", {
            currentLevel,
            totalLevels,
            currentPairs: matchedPairsInLevel,
            totalPairs: pairsInCurrentLevel,
            defaultValue: `Level ${currentLevel}/${totalLevels} - ${matchedPairsInLevel} / ${pairsInCurrentLevel} pairs`,
          })}
        </span>
        {combo > 1 && (
          <motion.span
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 font-semibold text-sm border border-amber-500/40"
          >
            {t("memoryMix.combo", {
              count: combo,
              defaultValue: `Combo x${combo}`,
            })}
          </motion.span>
        )}
      </div>

      <motion.div
        className="grid grid-cols-3 sm:grid-cols-4 gap-3 w-full flex-1 min-h-0 content-start"
        layout
      >
        <AnimatePresence mode="popLayout">
          {activeTiles.map((tile) => {
            const isFlipped = flippedTiles.has(tile.id);
            const isCelebrating = celebratingMatch.has(tile.id);
            return (
              <motion.div
                key={tile.id}
                layout
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  layout: { type: "spring", stiffness: 400, damping: 35 },
                  exit: { duration: 0.25 },
                }}
                className="relative aspect-[4/3] min-h-[80px]"
              >
                <motion.button
                  type="button"
                  layout
                  initial={false}
                  onClick={() => handleTileClick(tile)}
                  disabled={isFlippingBack || celebratingMatch.size > 0}
                  className={`relative w-full h-full rounded-xl border-2 overflow-hidden transition-colors disabled:pointer-events-none disabled:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] ${
                    isCelebrating
                      ? "border-green-600 bg-green-500 text-white dark:bg-green-600 dark:border-green-700"
                      : "border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] hover:border-[hsl(var(--primary)/0.6)]"
                  }`}
                  style={{ perspective: "1000px" }}
                  whileHover={
                    !isFlippingBack && !isCelebrating ? { scale: 1.02 } : undefined
                  }
                  whileTap={!isFlippingBack && !isCelebrating ? { scale: 0.98 } : undefined}
                >
                  <motion.div
                    className="absolute inset-0 w-full h-full"
                    style={{ transformStyle: "preserve-3d" }}
                    initial={false}
                    animate={{ rotateY: isFlipped ? 180 : 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                  >
                    <span
                      className={`absolute inset-0 flex items-center justify-center p-2 text-sm font-medium break-words ${
                        isCelebrating
                          ? "text-white bg-green-500 dark:bg-green-600"
                          : "text-[hsl(var(--foreground))]"
                      }`}
                      style={{
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        background: isCelebrating ? undefined : "hsl(var(--card-bg))",
                      }}
                    >
                      {tile.label}
                    </span>
                    <span
                      className={`absolute inset-0 flex items-center justify-center text-xl font-medium ${
                        isCelebrating ? "text-white bg-green-500 dark:bg-green-600" : "text-[hsl(var(--muted-foreground))]"
                      }`}
                      style={{
                        backfaceVisibility: "hidden",
                        background: isCelebrating ? undefined : "hsl(var(--muted)/0.4)",
                      }}
                    >
                      ?
                    </span>
                  </motion.div>
                </motion.button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
